/*
 * @providesModule BalanceBarV1
 *
 */

import React from 'react';

import _ from 'lodash';
import constants from 'HomeScreenConstants';
import AssetHelper from 'AssetHelper';
import ClientPropertyConstants from 'ClientPropertyConstants';
import GlobalEventEmitter from 'GlobalEventEmitter';
import I18n from 'INTL';
import NativeNavigation from 'NativeNavigation';
import NavigationManager from 'NavigationManager';
import PropTypes from 'prop-types';
import Sizes from 'Sizes';
import SKZConstants from 'SKZConstants';
import styles from 'BalanceBarV1Style';
import {
  ZCountingLabel,
  CashCountingLabel,
  TicketzCountingLabel,
  MedalCountingLabel,
} from 'SkillzCountingLabels';
import {IAnimatedShowerProps} from 'AnimatedShower';
import {IAnimatedBurstShowerProps} from '@skillz/ui';
import SkillzStyle from 'SkillzStyle';

import {
  findNodeHandle,
  NativeModules,
  Platform,
  StyleSheet,
  TouchableHighlight,
  View,
} from 'react-native';

const RCTUIManager = NativeModules.UIManager;

const TEST_IDS = {
  BALANCE_BAR: 'balance_bar',
};

const CASHBALANCE_PADDING_LENGTH = 7;
const CurrencyTypes = SKZConstants.CurrencyTypes;

export const getIconURL = (): string => {
  const currentCurrency = I18n.currentCurrency();
  switch (currentCurrency) {
    case 'INR':
      return AssetHelper.pathForAssetNamed('ic_cash_skz_inr.png', false);
    default:
      return AssetHelper.pathForAssetNamed('ic_cash_skz.png', false);
  }
};

export const requestBurstShowerAnimation = (
  config: Array<{currencyType: CurrencyTypes; options: Object}>,
  cashRef: React.Component<any, any>,
  zRef: React.Component<any, any>,
  ticketRef: React.Component<any, any>
) => {
  let currencyRef = null;
  const promises = [];
  _.forEach(config, (configItem: {currencyType: CurrencyTypes; options: Object}) => {
    let currencyImg = null;
    switch (configItem.currencyType) {
      case CurrencyTypes.CASH: {
        currencyRef = cashRef;
        currencyImg = {uri: getIconURL()};
        break;
      }
      case CurrencyTypes.Z: {
        currencyImg = {
          uri: AssetHelper.pathForAssetNamed(
            SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI
          ),
        };
        currencyRef = zRef;
        break;
      }
      case CurrencyTypes.TICKETZ: {
        currencyImg = {uri: AssetHelper.pathForAssetNamed('ic_ticketz_background.png', false)};
        currencyRef = ticketRef;
        break;
      }
    }
    const promise = new Promise(
      (resolve: (prop: IAnimatedBurstShowerProps) => void, reject: (error: Error) => void) => {
        const node = findNodeHandle(currencyRef);
        const currencyCap = configItem.currencyType == CurrencyTypes.CASH ? 7 : 9;
        if (!node) {
          console.warn('BalanceBarV1: Burst shower node undefined');
        }

        RCTUIManager.measure(
          node,
          (
            x: number,
            y: number,
            width: number,
            height: number,
            pagePointX: number,
            pagePointY: number
          ) => {
            let endXOffset = 2;
            let endYOffset = 5;

            // cash offsets
            if (configItem.currencyType == CurrencyTypes.CASH && Platform.OS == 'android') {
              endXOffset = 10;
              endYOffset = 2;
            } else if (configItem.currencyType == CurrencyTypes.CASH) {
              endXOffset = 5;
              endYOffset = 0;
            }

            // z offsets
            if (configItem.currencyType == CurrencyTypes.Z && Platform.OS == 'ios') {
              endXOffset = 5;
              endYOffset = 3;
            } else if (configItem.currencyType == CurrencyTypes.Z) {
              endXOffset = 4;
              endYOffset = 3;
            }
            const widthXFallback = Number(pagePointX) + 7;
            const delayVCount = configItem.options.iconCount < 8 ? 75 : 50;
            const startingX = configItem.options.x;
            const startingY = configItem.options.y;
            const endingX = width !== 0 ? Math.floor(pagePointX) - endXOffset : widthXFallback;
            const endingY = Math.floor(pagePointY) - endYOffset;
            const controlX = Math.max(startingX, endingX);
            const scaleItems = ClientPropertyConstants.isCoreLoopTungstenLiteEnabled()
              ? [0.4, 1.5, 1.1, 1]
              : [0.4, 1.5, 1, 0.7];
            const params = {
              icon: currencyImg,
              startingPoint: {x: startingX, y: startingY},
              endingPoint: {
                x: endingX,
                y: endingY,
              },
              iconCount:
                parseInt(configItem.options.iconCount) < currencyCap
                  ? parseInt(configItem.options.iconCount)
                  : currencyCap,
              animatedValue: {
                startingVariant: {x: 40, y: 40},
                delayV: delayVCount,
                duration: 700,
                delay: 500,
                endDelay: 170,
                itemSize: 30,
                scaleItems: scaleItems,
                opacity: [0, 1],
                control: {x: controlX, y: startingY},
                fadeOut: false,
                scaleUp: false,
              },
            };
            resolve(params);
          }
        );
      }
    );
    promises.push(promise);
  });
  Promise.all(promises).then((showerArray: IAnimatedBurstShowerProps[]) => {
    GlobalEventEmitter.emit('commitBurstShowerAnimations', showerArray);
  });
};

export const requestShowerAnimation = (
  config: Array<{currencyType: CurrencyTypes; options: Object}>,
  cashRef: React.Component<any, any>,
  zRef: React.Component<any, any>,
  ticketRef: React.Component<any, any>
) => {
  let currencyRef = null;
  const promises = [];
  _.forEach(config, (configItem: {currencyType: CurrencyTypes; options: Object}) => {
    let currencyImg = null;
    switch (configItem.currencyType) {
      case CurrencyTypes.CASH: {
        currencyRef = cashRef;
        currencyImg = getIconURL();
        break;
      }
      case CurrencyTypes.Z: {
        currencyImg = SkillzStyle.currencyValues.practiceCurrencyFullIconLargeURI;
        currencyRef = zRef;
        break;
      }
      case CurrencyTypes.TICKETZ: {
        currencyImg = AssetHelper.pathForAssetNamed('ic_ticketz_background.png', false);
        currencyRef = ticketRef;
        break;
      }
    }
    const promise = new Promise(
      (resolve: (prop: IAnimatedShowerProps) => void, reject: (error: Error) => void) => {
        const node = findNodeHandle(currencyRef);
        const currencyCap = configItem.currencyType == CurrencyTypes.CASH ? 200 : 250;
        if (!node) {
          console.warn('BalanceBarV1: Shower node undefined');
        }
        RCTUIManager.measure(
          node,
          (
            x: number,
            y: number,
            width: number,
            height: number,
            pagePointX: number,
            pagePointY: number
          ) => {
            let endXOffset = 5;
            if (configItem.currencyType == CurrencyTypes.CASH && Platform.OS == 'android') {
              endXOffset = 10;
            }
            const widthXFallback = Number(pagePointX) + 7;
            const durationVCap = configItem.options.currencyCount < 50 ? 200 : 50;
            const params = {
              icon: currencyImg,
              startingPoint: {x: configItem.options.x, y: configItem.options.y},
              endingPoint: {
                x: width !== 0 ? Math.floor(pagePointX) - endXOffset : widthXFallback,
                y: Math.floor(pagePointY) - 1,
              },
              animationCount:
                parseInt(configItem.options.currencyCount) < currencyCap
                  ? parseInt(configItem.options.currencyCount)
                  : currencyCap,
              animatedValue: {
                startingVariant: {x: 20, y: 20},
                durationV: durationVCap,
                duration: 700,
                delay: 300,
                itemSize: 30,
                scaleItems: [0.4, 0.7, 1, 0.7],
                opacity: [0.5, 1, 1, 0],
              },
            };
            resolve(params);
          }
        );
      }
    );
    promises.push(promise);
  });
  Promise.all(promises).then((showerArray: IAnimatedShowerProps[]) => {
    GlobalEventEmitter.emit('commitShowerAnimations', showerArray);
  });
};

export default class BalanceBar extends React.Component {
  static propTypes = {
    shouldAnimate: PropTypes.bool,
    forceAnimation: PropTypes.bool,
    animationCurve: PropTypes.string,
    animationDuration: PropTypes.number,
    animationPrecisions: PropTypes.object,
    cashBalance: PropTypes.number.isRequired,
    zBalance: PropTypes.number.isRequired,
    ticketzBalance: PropTypes.number.isRequired,
    medalBalance: PropTypes.number,
    currencyLayout: PropTypes.func,
    borderType: PropTypes.oneOf(['surround', 'divider', 'none']),
    borderColor: PropTypes.string,
    currencyOrder: PropTypes.arrayOf(CurrencyTypes),
    style: PropTypes.object,
    isTouchable: PropTypes.bool,
    disableEvents: PropTypes.bool,
    identifier: PropTypes.string,
  };

  static defaultProps = {
    shouldAnimate: true,
    borderType: 'none',
    currencyOrder: [CurrencyTypes.TICKETZ, CurrencyTypes.Z, CurrencyTypes.CASH],
    style: {height: constants.getBalanceBarHeightV1()},
    isTouchable: true,
    animationPrecisions: {z: 0, cash: 2, ticketz: 0},
    animationCurve: 'ease-in',
    animationDuration: 2000,
    identifier: '',
    forceAnimation: false,
  };

  constructor(props: Object) {
    super(props);
    const viewStyle =
      this.props.borderType.toLowerCase() == 'surround' ? styles.balanceViewBorder : {};
    this.flattenedStyle = StyleSheet.flatten([styles.balanceView, viewStyle, this.props.style]);
    this.backgroundColor = this.flattenedStyle.backgroundColor;
  }

  componentDidMount() {
    if (!this.props.disableEvents) {
      this.currencyFlyingListener = GlobalEventEmitter.addListener(
        'requestShowerAnimation',
        (config: Array<{currencyType: CurrencyTypes; options: Object}>) => {
          requestShowerAnimation(config, this.cashRef, this.zCoinRef, this.ticketzRef);
        }
      );
      this.currencyBurstFlyingListener = GlobalEventEmitter.addListener(
        'requestBurstShowerAnimation',
        (config: Array<{currencyType: CurrencyTypes; options: Object}>) => {
          requestBurstShowerAnimation(config, this.cashRef, this.zCoinRef, this.ticketzRef);
        }
      );
    }
  }

  componentWillUnmount() {
    if (!this.props.disableEvents) {
      this.currencyFlyingListener?.remove();
      this.currencyFlyingListener = null;
      this.currencyBurstFlyingListener?.remove();
      this.currencyBurstFlyingListener = null;
    }
  }

  _updateBalanceViewLayout(e: unknown) {
    e.persist();

    if (!this.props.currencyLayout) {
      return;
    }

    const balanceLayout = e.nativeEvent.layout;
    balanceLayout.rootX = e.nativeEvent.layout.x;
    balanceLayout.rootY = 0;
    if (Sizes.isLandscape()) {
      if (Sizes.isTablet()) {
        balanceLayout.width = Sizes.getUIWidth();
        balanceLayout.height = 0;
      } else {
        balanceLayout.rootY = e.nativeEvent.layout.y + constants.MENU_HEIGHT;
        balanceLayout.width = 0;
        balanceLayout.height = e.nativeEvent.layout.height;
        balanceLayout.rootX = 0;
      }
    } else {
      balanceLayout.height = 0;
    }
    this.props.currencyLayout(balanceLayout);
  }

  /*13 is the max characters that can reside in the currency cell without shrinking the font size.
  This size includes the formatting and the currency icon added at the front.*/
  _renderContainerStyle(type: string): StyleSheet.Element {
    const cashBalance = this.props.cashBalance;
    if (cashBalance.length >= CASHBALANCE_PADDING_LENGTH && Sizes.isPhone()) {
      //Nested ifs to calculate the width% for each cell depending on the cash balance digits
      let cashWidthPercentage = '';
      let ticketZWidthPercentage = '';
      if (cashBalance.length === CASHBALANCE_PADDING_LENGTH) {
        cashWidthPercentage = '40%';
        ticketZWidthPercentage = '30%';
      } else if (cashBalance.length === CASHBALANCE_PADDING_LENGTH + 1) {
        cashWidthPercentage = '42%';
        ticketZWidthPercentage = '29%';
      } else if (cashBalance.length === CASHBALANCE_PADDING_LENGTH + 2) {
        cashWidthPercentage = '44%';
        ticketZWidthPercentage = '28%';
      } else {
        cashWidthPercentage = '46%';
        ticketZWidthPercentage = '27%';
      }
      switch (type.toLowerCase()) {
        case 'cash':
          return StyleSheet.flatten([
            styles.cashContainer,
            {
              width: cashWidthPercentage,
            },
          ]);
        default:
          return StyleSheet.flatten([
            styles.ticketzZBalanceContainer,
            {
              width: ticketZWidthPercentage,
            },
          ]);
      }
    } else {
      return styles.balanceContainer;
    }
  }

  _renderBalance(
    key: string,
    onPress: () => {},
    accessibilityLabel: string,
    testID: string,
    countingLabel: React.ReactNode,
    index: number
  ): React.ReactNode {
    const borderStyle = styles['balanceBorder_' + this.props.borderType.toLowerCase()];
    const dividerStyle =
      Sizes.isPortrait() && Sizes.isTablet()
        ? index === 1
          ? styles.balanceBorderPortraitTabletCenter
          : styles.balanceBorderPortraitTabletNone
        : {};
    const containerStyle = this._renderContainerStyle(key);
    const dividerColor = this.props.borderColor ? {borderColor: this.props.borderColor} : {};
    const backgroundColor = {backgroundColor: this.backgroundColor};
    return (
      <TouchableHighlight
        activeOpacity={0.5}
        style={[containerStyle, borderStyle, dividerStyle, dividerColor, backgroundColor]}
        onPress={onPress}
        accessible={true}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={'button'}
        testID={testID}
        key={key}
      >
        <View style={[styles.counterView, backgroundColor]}>
          <View style={styles.countingLabelContainer}>{countingLabel}</View>
        </View>
      </TouchableHighlight>
    );
  }

  _renderMedalBalance(index: number): React.ReactNode {
    const onPressFunction = () => {};

    return this._renderBalance(
      'medals',
      onPressFunction,
      I18n.message('Medal balance {0}', this.props.medalBalance),
      'medal_balance',
      <MedalCountingLabel
        style={styles.countingLabel}
        medalLayout={(e: unknown) => {
          /*RN Measure Workaround for Android*/
        }}
        value={this.props.medalBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.ticketz}
        medalRef={(ref) => {
          this.medalRef = ref;
        }}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
      />,
      index
    );
  }

  _renderTicketzBalance(index: number): React.ReactNode {
    let onPressFunction = null;
    if (this.props.isTouchable) {
      onPressFunction = () => {
        NavigationManager.displayTicketz();
      };
    }

    return this._renderBalance(
      'ticketz',
      onPressFunction,
      I18n.message('Ticketz balance {0}', this.props.ticketzBalance),
      'ticketz_balance',
      <TicketzCountingLabel
        style={styles.countingLabel}
        ticketzLayout={(e: unknown) => {
          /*RN Measure Workaround for Android*/
        }}
        value={this.props.ticketzBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.ticketz}
        ticketzRef={(ref) => {
          this.ticketzRef = ref;
        }}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
      />,
      index
    );
  }

  _renderZBalance(index: number): React.ReactNode {
    let onPressFunction = null;
    if (this.props.isTouchable) {
      onPressFunction = () => {
        NavigationManager.popToRoot();
        NativeNavigation.gotoDestination('Store');
      };
    }

    return this._renderBalance(
      'z',
      onPressFunction,
      I18n.message('Z balance {0}', this.props.zBalance),
      'z_balance',
      <ZCountingLabel
        style={styles.countingLabel}
        coinLayout={(e: unknown) => {
          /*RN Measure Workaround for Android*/
        }}
        coinRef={(ref) => {
          this.zCoinRef = ref;
        }}
        value={this.props.zBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.z}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
      />,
      index
    );
  }

  _renderCashBalance(index: number): React.ReactNode | null {
    let onPressFunction = null;
    if (this.props.isTouchable) {
      onPressFunction = () => {
        NavigationManager.popToRoot();
        NativeNavigation.gotoDestination('Store');
      };
    }
    if (ClientPropertyConstants.getIsVirtualCurrencyOnly()) {
      return false;
    }
    return this._renderBalance(
      'cash',
      onPressFunction,
      I18n.message('Cash balance {0}', I18n.getCurrencyString(this.props.cashBalance, '$')),
      'cash_balance',
      <CashCountingLabel
        style={styles.countingLabel}
        cashLayout={(e: unknown) => {
          /*RN Measure Workaround for Android*/
        }}
        cashRef={(ref) => {
          this.cashRef = ref;
        }}
        value={this.props.cashBalance}
        animationCurve={this.props.animationCurve}
        animationDuration={this.props.animationDuration}
        animationPrecision={this.props.animationPrecisions?.cash}
        shouldAnimate={this.props.shouldAnimate}
        identifier={this.props.identifier}
        forceAnimation={this.props.forceAnimation}
        padding={this.props.cashBalance.length >= CASHBALANCE_PADDING_LENGTH ? 4 : 0}
      />,
      index
    );
  }

  _renderBalanceForType(type: CurrencyTypes, index: number): React.ReactNode {
    switch (type) {
      case CurrencyTypes.CASH:
        return this._renderCashBalance(index);
      case CurrencyTypes.TICKETZ:
        return this._renderTicketzBalance(index);
      case CurrencyTypes.Z:
        return this._renderZBalance(index);
      case CurrencyTypes.MEDAL:
        return this._renderMedalBalance(index);
    }
  }

  render(): React.ReactNode {
    return (
      <View
        testID={TEST_IDS.BALANCE_BAR}
        style={this.flattenedStyle}
        onLayout={this._updateBalanceViewLayout.bind(this)}
      >
        {this.props.currencyOrder.map((type: CurrencyTypes, index: number): React.ReactNode => {
          return this._renderBalanceForType(type, index);
        })}
      </View>
    );
  }
}
